/**
 * Native-menu accelerator labels derived from a keymap.
 *
 * PURE MODULE. `useAppMenuConfigSync` pushes `appMenuAccelerators` of the
 * effective keymap to main. `defaultAppMenuAccelerators` is that push for a
 * scheme with no overrides. Main builds its first menu from the config it saved
 * last time, or from its copy of the Jam defaults
 * (`electron/appMenuDefaultConfig.ts`, kept equal by `electron/appMenu.test.ts`),
 * so the startup push does not rebuild the menu.
 */
import { chordToElectronAccelerator } from '../../features/keybindings/model/electronAccelerator';
import {
  defaultKeybindings,
  keybindingCommandIds,
} from '../../features/keybindings/model/defaultKeybindings';
import { resolveKeymap } from '../../features/keybindings/model/resolveKeymap';
import { jamScheme } from '../../features/keybindings/model/schemes/jam';
import type { KeybindingScheme, Keymap, Platform } from '../../features/keybindings/model/keybindingTypes';

/** Menu command id → the first of its command's chords Electron can show. */
export function appMenuAccelerators(keymap: Keymap, targetPlatform: Platform): Map<string, string> {
  const accelerators = new Map<string, string>();

  for (const commandId of keybindingCommandIds) {
    const menuCommand = defaultKeybindings[commandId].menuCommand;
    if (!menuCommand) continue;
    for (const chord of keymap[commandId]) {
      const accelerator = chordToElectronAccelerator(chord, targetPlatform);
      if (!accelerator) continue;
      accelerators.set(menuCommand, accelerator);
      break;
    }
  }

  return accelerators;
}

/** The accelerators the renderer pushes for a scheme with no overrides (the Jam scheme by default). */
export function defaultAppMenuAccelerators(
  targetPlatform: Platform,
  scheme: KeybindingScheme = jamScheme,
): Map<string, string> {
  return appMenuAccelerators(resolveKeymap(scheme, {}, targetPlatform), targetPlatform);
}
